/**
 * Account-aware navigation primitives (ADR-0302).
 *
 * Authenticated tenant pages live under `/a/<accountPublicId>/…`. Code all over
 * the app navigates to absolute tenant paths such as `/activation/segments`;
 * these wrappers put the tab's account in front of such a path, so a call site
 * needs an import change and no path rewrite, and a path written tomorrow is
 * prefixed too instead of silently leaving the account.
 *
 * `useNavigate`, `Link`, `NavLink` and `Navigate` must come from here, never
 * from `react-router-dom` (the guard in `__tests__/tenantRouting.guard.test.ts`
 * enforces that). Everything else from react-router-dom is used directly.
 */

import { forwardRef, useCallback, useRef } from "react";
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  Navigate as RouterNavigate,
  useInRouterContext,
  useLocation,
  useNavigate as useRouterNavigate,
  type LinkProps,
  type NavigateFunction,
  type NavigateOptions,
  type NavigateProps,
  type NavLinkProps,
  type Path,
  type To,
} from "react-router-dom";

import { tabAccountFromPath } from "@/api/client";
import { getSessionAccount } from "@/api/sessionAccount";
import { useAuth } from "@/context/AuthContext";
import { stripTenantPrefix, tenantPath } from "@/routing/tenantPaths";

export {
  isTenantPath,
  stripTenantPrefix,
  TENANT_ROUTE_BASE,
  tenantPath,
  UNPREFIXED_ROOTS,
} from "@/routing/tenantPaths";

function tenantTo(to: To, account: string | null | undefined): To {
  if (typeof to === "string") return tenantPath(to, account);
  if (!to.pathname) return to;
  const pathname = tenantPath(to.pathname, account);
  return pathname === to.pathname ? to : ({ ...to, pathname } satisfies Partial<Path>);
}

/**
 * The router's pathname, or `window.location`'s outside a router. Providers
 * and hooks that navigate are rendered in tests without a router (with
 * `useNavigate` mocked); the wrappers must not be the thing that breaks there.
 * Whether a component is inside a router never changes during its life, so
 * the conditional hook call is stable.
 */
function useCurrentPathname(): string {
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return inRouter ? useLocation().pathname : window.location.pathname;
}

/**
 * The account the wrappers prefix with: the tab's URL account, else (on an
 * unprefixed page such as a superadmin page) the session's active account.
 */
export function useTabAccount(): string | null {
  const pathname = useCurrentPathname();
  const { activeAccount } = useAuth();
  return tabAccountFromPath(pathname) ?? activeAccount?.id ?? null;
}

/**
 * `useLocation().pathname` without the account prefix, for code that compares
 * the current path with a tenant root (`/insights/…`, `/activation/…`).
 */
export function useTenantPathname(): string {
  return stripTenantPrefix(useLocation().pathname);
}

/**
 * `useNavigate`, prefixing at CALL time. A handler that runs after an await
 * must not prefix with the account of the render it was created in: on
 * `/accept-invite`, `await switchAccount(B); navigate("/")` has to land in B.
 * So the account is read when navigating: the tab's current URL, else the
 * session account the switch just recorded, else the rendered active account.
 */
export function useNavigate(): NavigateFunction {
  const navigate = useRouterNavigate();
  const pathname = useCurrentPathname();
  const { activeAccount } = useAuth();
  const current = useRef({ pathname, fallback: activeAccount?.id ?? null });
  current.current = { pathname, fallback: activeAccount?.id ?? null };
  return useCallback(
    ((to: To | number, options?: NavigateOptions) => {
      if (typeof to === "number") return navigate(to);
      // Where the tab IS, first: a component can hold this function across an
      // account switch without re-rendering (a load that fails after the
      // switch moved the tab, then `navigate("/list")` in its catch), and its
      // last render names the old account. In the browser `window.location`
      // is always current; the render-time pathname only serves routers that
      // do not drive it (MemoryRouter in tests).
      const account =
        tabAccountFromPath(window.location.pathname) ??
        tabAccountFromPath(current.current.pathname) ??
        getSessionAccount() ??
        current.current.fallback;
      return navigate(tenantTo(to, account), options);
    }) as NavigateFunction,
    [navigate],
  );
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link({ to, ...rest }, ref) {
  const account = useTabAccount();
  return <RouterLink ref={ref} to={tenantTo(to, account)} {...rest} />;
});

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink(
  { to, ...rest },
  ref,
) {
  const account = useTabAccount();
  return <RouterNavLink ref={ref} to={tenantTo(to, account)} {...rest} />;
});

export function Navigate({ to, ...rest }: NavigateProps) {
  const account = useTabAccount();
  return <RouterNavigate to={tenantTo(to, account)} {...rest} />;
}

/**
 * Old unprefixed tenant URLs (bookmarks, links already mailed) land here and
 * move into the session's account, keeping search and hash. Before this plan
 * they opened in whatever account the session was on; that stays true, no
 * worse.
 */
export function LegacyTenantRedirect() {
  const { pathname, search, hash } = useLocation();
  const { activeAccount } = useAuth();
  if (!activeAccount) return <RouterNavigate to="/login" replace />;
  const target = tenantPath(pathname, activeAccount.id);
  // A path that cannot be prefixed (a bare `/a`, an unknown unprefixed root)
  // would redirect to itself forever; send it to the account's landing page.
  if (target === pathname) return <RouterNavigate to={`/a/${activeAccount.id}`} replace />;
  return <RouterNavigate to={`${target}${search}${hash}`} replace />;
}
